import { supabase } from "@/integrations/supabase/client";
import Logger from "@/utils/logger";
import type { ClientFullData, ClientListData, ClientStage, ClientHealthMetrics, ClientActivityFeedItem, ActivityEventType } from "@/types/client";
import type { Tables } from "@/integrations/supabase/types";

const SERVICE_COMPONENT = 'clientService';

/**
 * Fetches a list of clients for the current user.
 * Uses the `clients_list` view.
 */
export async function fetchClientsList(): Promise<ClientListData[]> {
  const { data, error } = await supabase
    .from('clients_list') // Use the new view
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .select('id, name, description, domain, physical_address, stage' as any)
    .order('name', { ascending: true });

  if (error) {
    Logger.error('Failed to fetch clients list from view', error, { component: SERVICE_COMPONENT });
    throw error;
  }
  return (data || []) as unknown as ClientListData[];
}

/**
 * Fetches detailed configuration for a single client.
 * Uses the `get_client_configuration` RPC.
 */
export async function fetchClientDetails(clientId: string): Promise<ClientFullData | null> {
  const { data, error } = await supabase
    .rpc('get_client_configuration', { p_client_id: clientId })
    .maybeSingle(); // Expects a single object or null

  if (error) {
    Logger.error('Failed to fetch client details via RPC', error, { component: SERVICE_COMPONENT, clientId });
    throw error;
  }
  
  // The RPC returns a row from client_configuration view which should match ClientFullData.
  // Explicit type casting might be desired if Supabase types are generic.
  return data as ClientFullData | null;
}

/**
 * Creates a new client and their associated configurations.
 * Uses the `create_client_full` RPC.
 * @param clientData - The partial data for the client. Only customized fields should be provided. owner_id and id are ignored.
 */
export async function createClient(clientData: Partial<Omit<ClientFullData, 'id' | 'owner_id'>>): Promise<ClientFullData> {
  // The RPC will set owner_id from auth.uid() and generate the client_id.
  const payload = { ...clientData };
  // Remove fields that RPC doesn't expect at top level or handles internally
  delete (payload as Partial<Record<string, unknown>>).id;
  delete (payload as Partial<Record<string, unknown>>).owner_id;


  const { data: newClientData, error } = await supabase
    .rpc('create_client_full', { p_config: payload })
    .single(); // Expects the created client's full data back

  if (error) {
    Logger.error('Failed to create new client via RPC', error, { component: SERVICE_COMPONENT });
    if (error.message.includes('clients_name_owner_id_unique')) {
      throw new Error('A client with this name already exists.');
    }
    throw new Error(error.message);
  }
  if (!newClientData) {
    Logger.error('Client creation via RPC returned no data', undefined, { component: SERVICE_COMPONENT });
    throw new Error('Client creation failed, no data returned from RPC.');
  }

  return newClientData as ClientFullData;
}


/**
 * Updates an existing client and their associated configurations.
 * Uses the `update_client_full` RPC.
 * @param clientId - The ID of the client to update.
 * @param clientData - The data to update. Only provided fields will be changed.
 */
export async function updateClient(clientId: string, clientData: Partial<ClientFullData>): Promise<ClientFullData> {
  const payload = { ...clientData };
  // Remove fields that RPC doesn't expect at top level or handles internally
  delete (payload as Partial<Record<string, unknown>>).id;
  delete (payload as Partial<Record<string, unknown>>).owner_id;

  const { data: updatedClientData, error } = await supabase
    .rpc('update_client_full', {
      p_client_id: clientId,
      p_config: payload
    })
    .single(); // Expects the updated client's full data back

  if (error) {
    Logger.error('Failed to update client via RPC', error, { component: SERVICE_COMPONENT, clientId });
    if (error.message.includes('clients_name_owner_id_unique')) {
      throw new Error('A client with this name already exists.');
    }
    throw new Error(error.message);
  }
  if (!updatedClientData) {
    Logger.error('Client update via RPC returned no data', undefined, { component: SERVICE_COMPONENT, clientId });
    throw new Error('Client update failed, no data returned from RPC.');
  }
  
  return updatedClientData as ClientFullData;
}

/**
 * Deletes a client. Associated configurations should be deleted via CASCADE constraint.
 * This function can remain as is, as direct delete with RLS and CASCADE is standard.
 * @param clientId - The ID of the client to delete.
 */
export async function deleteClient(clientId: string): Promise<void> {
  const { error } = await supabase
    .from('clients') // Direct table delete is fine with RLS and CASCADE
    .delete()
    .eq('id', clientId);

  if (error) {
    Logger.error('Failed to delete client', error, { component: SERVICE_COMPONENT, clientId });
    throw error;
  }
}

/**
 * Calls the backend to generate a client description from a URL.
 */
export async function generateClientDescription(url: string): Promise<{ description: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('generate-description', {
      body: { url },
    });

    if (error) {
      Logger.error('Edge function returned error', error, { 
        component: SERVICE_COMPONENT, 
        url,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw error;
    }

    if (!data || !data.description) {
      Logger.error('Invalid response from edge function', undefined, { 
        component: SERVICE_COMPONENT, 
        url,
        data,
        hasData: !!data,
        hasDescription: !!(data?.description)
      });
      throw new Error('Received an empty description from the server.');
    }

    Logger.info('Client description generated successfully', { 
      component: SERVICE_COMPONENT, 
      url,
      descriptionLength: data.description.length,
      descriptionPreview: data.description.substring(0, 100) + '...'
    });

    return data;
  } catch (error) {
    Logger.error('Client description generation failed', error instanceof Error ? error : new Error(String(error)), {
      component: SERVICE_COMPONENT,
      url,
      errorType: error instanceof Error ? error.constructor.name : typeof error
    });
    throw error;
  }
}

/**
 * Updates the pipeline stage for a client.
 */
export async function updateClientStage(clientId: string, stage: ClientStage): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await supabase.from('clients').update({ stage } as any).eq('id', clientId);
  if (error) {
    Logger.error('Failed to update client stage', error, { component: SERVICE_COMPONENT, clientId, stage });
    throw error;
  }
}

/**
 * Fetches health metrics for a single client.
 */
export async function fetchClientHealthMetrics(clientId: string): Promise<ClientHealthMetrics | null> {
  const { data, error } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('client_health_metrics' as any)
    .select('*')
    .eq('client_id', clientId)
    .maybeSingle();

  if (error) {
    Logger.error('Failed to fetch client health metrics', error, { component: SERVICE_COMPONENT, clientId });
    throw error;
  }
  return data as unknown as ClientHealthMetrics | null;
}

/**
 * Fetches the activity feed for a client, with optional filtering and pagination.
 */
export async function fetchClientActivityFeed(
  clientId: string,
  options?: { eventTypes?: ActivityEventType[]; limit?: number; offset?: number }
): Promise<ClientActivityFeedItem[]> {
  let query = supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('client_activity_feed' as any)
    .select('*')
    .eq('client_id', clientId)
    .order('event_date', { ascending: false });

  if (options?.eventTypes?.length) query = query.in('event_type', options.eventTypes);
  if (options?.limit) query = query.limit(options.limit);
  if (options?.offset) query = query.range(options.offset, options.offset + (options?.limit ?? 50) - 1);

  const { data, error } = await query;
  if (error) {
    Logger.error('Failed to fetch client activity feed', error, { component: SERVICE_COMPONENT, clientId });
    throw error;
  }
  return (data || []) as unknown as ClientActivityFeedItem[];
}

/**
 * Fetches bookings for a client from the booking_details view.
 */
export async function fetchClientBookings(clientId: string) {
  const { data, error } = await supabase
    .from('booking_details')
    .select('id, name, start_date, end_date, status_code, venue_name')
    .eq('client_id', clientId)
    .order('start_date', { ascending: false });

  if (error) {
    Logger.error('Failed to fetch client bookings', error, { component: SERVICE_COMPONENT, clientId });
    throw error;
  }
  return (data || []) as Array<{
    id: string;
    name: string | null;
    start_date: string | null;
    end_date: string | null;
    status_code: string | null;
    venue_name: string | null;
  }>;
}

/**
 * Fetches contacts linked to a client via client_contacts → user_contacts join.
 */
export async function fetchClientContacts(clientId: string) {
  const { data, error } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('client_contacts' as any)
    .select('contact_id, contact:user_contacts(id, first_name, last_name, position, email, cell_phone)')
    .eq('client_id', clientId);

  if (error) {
    Logger.error('Failed to fetch client contacts', error, { component: SERVICE_COMPONENT, clientId });
    throw error;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data || []) as any[]).map((row: any) => {
    const c = row.contact ?? {};
    return {
      id: c.id ?? row.contact_id,
      name: [c.first_name, c.last_name].filter(Boolean).join(' ') || null,
      title: c.position ?? null,
      email: c.email ?? null,
      phone: c.cell_phone ?? null,
    };
  });
}

/**
 * Fetches invoices for a client, ordered by issued_date descending.
 */
export async function fetchClientInvoices(clientId: string) {
  const { data, error } = await supabase
    .from('booking_invoices')
    .select('id, booking_id, total_amount, invoice_date, due_date, status_id')
    .eq('client_id', clientId)
    .order('invoice_date', { ascending: false });

  if (error) {
    Logger.error('Failed to fetch client invoices', error, { component: SERVICE_COMPONENT, clientId });
    throw error;
  }
  return (data || []).map((row) => ({
    id: row.id,
    booking_id: row.booking_id,
    total_amount: row.total_amount,
    status_id: row.status_id,
    invoice_date: row.invoice_date,
    due_date: row.due_date,
  }));
}

/**
 * Fetches health metrics for all clients (for list view).
 */
export async function fetchAllClientHealthMetrics(): Promise<ClientHealthMetrics[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await supabase.from('client_health_metrics' as any).select('*');
  if (error) {
    Logger.error('Failed to fetch all client health metrics', error, { component: SERVICE_COMPONENT });
    throw error;
  }
  return (data || []) as unknown as ClientHealthMetrics[];
}
